import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { LocalImageProps } from '@/components/Common/LocalImage';
import { cn } from '@/lib/utils';
import { InstagramQueryPostItemModel } from '@/types/anonymously.interface';
import PostVideo from '../PostVideo';
import PostImage from '../PostImage';

const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), {
  ssr: false,
});

interface PostContentProps {
  item: InstagramQueryPostItemModel;
  /** 是否为移动端预览，仅展示简单内容 */
  isMobilePreview?: boolean;
  /** 是否为移动端查看，会展示完整内容 */
  isMobileView?: boolean;
  /** 查看更多点击事件，用于触发显示帖子详情弹层，或跳转帖子详情页面等 */
  onViewMoreClick?: () => void;
}

// Instagram 帖子内容
const PostContent = ({ item, isMobilePreview, isMobileView, onViewMoreClick }: PostContentProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const captionRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = () => {
    if (onViewMoreClick) {
      // 移动端触发外部事件
      onViewMoreClick?.();
    } else {
      setExpanded(!expanded);
    }
  };

  // 轮播图导航
  const nextSlide = () => {
    if (item?.carousel_media && item.carousel_media.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % item.carousel_media.length);
    }
  };

  const prevSlide = () => {
    if (item?.carousel_media && item.carousel_media.length > 0) {
      setCurrentSlide((prev) => (prev === 0 ? item.carousel_media.length - 1 : prev - 1));
    }
  };

  // 处理caption可能是对象的情况
  const getCaptionText = () => {
    if (!item?.caption) return '';

    // 如果caption是对象且有text属性
    if (typeof item.caption === 'object' && item.caption !== null) {
      return (item.caption as any).text || '';
    }

    // 如果caption是字符串
    return String(item.caption);
  };

  const captionText = getCaptionText();

  // 检测文本是否超出一行的函数
  const checkTextOverflow = () => {
    if (captionRef.current) {
      const isTextOverflowing = captionRef.current.scrollHeight > captionRef.current.clientHeight;
      setIsOverflowing(isTextOverflowing);
    }
  };

  // 监听文本变化时检测是否溢出
  useEffect(() => {
    checkTextOverflow();

    // 使用requestAnimationFrame确保DOM已更新
    const frameId = requestAnimationFrame(() => {
      checkTextOverflow();
    });

    return () => cancelAnimationFrame(frameId);
  }, [captionText, expanded]);

  // item 变化时重置轮播状态，并在下一帧检测文本溢出
  useEffect(() => {
    setCurrentSlide(0);
    setExpanded(false);

    // 使用setTimeout确保DOM完全更新后再检测
    const timerId = setTimeout(() => {
      checkTextOverflow();
    }, 0);

    return () => clearTimeout(timerId);
  }, [item]);

  // 窗口尺寸变化时重新检测
  useEffect(() => {
    const handleResize = () => {
      checkTextOverflow();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderMediaContent = () => {
    // INFO: 后续将支持图片和视频轮播
    // 轮播图内容 - 使用carousel_media
    if (item?.carousel_media && item.carousel_media.length > 1) {
      return (
        <div className="h-full w-full relative flex justify-center items-center">
          {/* 当前显示的图片 */}
          {item.carousel_media[currentSlide]?.media_type == 1 && (
            <PostImage
              src={item.carousel_media[currentSlide]?.thumbnail_url || ''}
              alt={`Slide ${currentSlide + 1}`}
              style={{
                height: isMobileView ? 375 : undefined,
              }}
            />
          )}

          {item.carousel_media[currentSlide]?.media_type == 2 && (
            <PostVideo
              src={item.carousel_media[currentSlide]?.media_url}
              poster={item.carousel_media[currentSlide]?.thumbnail_url}
              rootClassName={cn({ 'h-[375px]': isMobileView })}
              isShowVedioControls={false}
            />
          )}

          {/* 左右导航按钮 */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center z-10"
            aria-label="Previous slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center z-10"
            aria-label="Next slide"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          {/* 轮播指示器 */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-1 z-10">
            {item.carousel_media.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${currentSlide === index ? 'bg-white' : 'bg-white/50'}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>

          {/* 数量指示 */}
          <div className="absolute top-2.5 right-2.5 bg-black/50 text-white px-2 py-1 rounded text-xs">
            {currentSlide + 1}/{item.carousel_media.length}
          </div>
        </div>
      );
    }

    // 视频内容
    if (item?.is_video && item?.video_url) {
      return (
        <PostVideo src={item.video_url} poster={item.thumbnail_url} rootClassName={cn({ 'h-[375px]': isMobileView })} />
      );
    }

    // 单图内容
    return (
      <PostImage
        rootClassName="relative flex justify-center items-center"
        src={item?.cover_image || item?.thumbnail_url || ''}
        alt={captionText || 'Instagram post'}
        style={{
          height: isMobileView ? 375 : undefined,
        }}
      />
    );
  };

  const renderMobileContent = () => {
    return (
      <div className="h-24" onClick={onViewMoreClick}>
        <div className="h-16 flex gap-3">
          {/* 左侧缩略图 */}
          <div className="w-16 h-full flex-shrink-0 bg-black rounded-md overflow-hidden">
            <PostImage src={item?.thumbnail_url || ''} alt={captionText || 'Instagram post'} />
          </div>

          {/* 右侧文本内容 */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-end justify-between">
              <div ref={captionRef} className="text-sm overflow-hidden text-ellipsis line-clamp-3 flex-1">
                {captionText}
              </div>

              {isOverflowing && (
                <button
                  onClick={toggleExpanded}
                  className="text-[#725CFF] bg-transparent border-none text-sm cursor-pointer ml-4 whitespace-nowrap hover:underline"
                >
                  {t('more')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="text-sm mt-3 text-[#6D6D6D]">
          {item?.publish_time
            ? dayjs(item.publish_time * 1000).format('YYYY-MM-DD HH:mm:ss')
            : dayjs(item?.taken_at * 1000).format('YYYY-MM-DD HH:mm:ss')}
        </div>
      </div>
    );
  };

  // 根据设备类型渲染不同内容
  if (isMobilePreview) {
    return <div className="h-full">{renderMobileContent()}</div>;
  }

  return (
    <div className="flex flex-col h-full relative bg-black">
      <div className="flex-1 h-[375px] flex justify-center items-center">{renderMediaContent()}</div>

      <div
        className={cn('absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black to-transparent text-white', {
          'relative bg-white text-inherit': isMobileView,
        })}
      >
        <div className="flex items-end justify-between mb-2">
          <div
            ref={captionRef}
            className={cn(`text-sm overflow-hidden text-ellipsis flex-1`, {
              'line-clamp-1': !expanded,
              'line-clamp-2': !expanded && isMobileView,
            })}
          >
            {captionText}
          </div>

          {(isOverflowing || expanded) && (
            <button
              onClick={toggleExpanded}
              className={cn(
                'bg-transparent border-none text-white text-xs cursor-pointer ml-8 whitespace-nowrap hover:underline',
                {
                  'ml-4 text-inherit': isMobileView,
                }
              )}
            >
              {expanded ? t('Collapse') : t('View more')}
            </button>
          )}
        </div>

        <div className={cn('text-xs text-white/80', { 'text-inherit': isMobileView })}>
          {item?.publish_time
            ? dayjs(item.publish_time * 1000).format('YYYY-MM-DD HH:mm:ss')
            : dayjs(item?.taken_at * 1000).format('YYYY-MM-DD HH:mm:ss')}
        </div>
      </div>
    </div>
  );
};

export default PostContent;
